import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["display", "input"]
  static values = {
    fieldPath: String,
    fieldType: String,
    currentValue: String,
    originalValue: String,
    precision: Number,
    readOnly: Boolean,
    selectOptions: Array
  }

  connect() {
    this.isEditing = false
    this._editorController = null

    // Set up click handler for editing if not read-only
    if (!this.readOnlyValue) {
      this.element.addEventListener('click', this.edit.bind(this))
      this.element.classList.add('cursor-pointer', 'hover:bg-gray-100', 'transition-colors', 'duration-200')
    }
  }

  // Lazy getter for editor controller - looks it up when needed instead of caching at connect time
  // Only caches when found, so it will retry if initially null
  get editorController() {
    if (!this._editorController) {
      const controller = this.findEditorController()
      if (controller) {
        this._editorController = controller
      }
      return controller
    }
    return this._editorController
  }

  disconnect() {
    if (this.inputElement) {
      this.inputElement.remove()
    }
  }

  edit() {
    if (this.readOnlyValue || this.isEditing) return

    this.isEditing = true
    this.element.classList.add('editing')

    // Create and show input element
    this.createInputElement()
    this.showInput()
    this.inputElement.focus()

    if (this.fieldTypeValue !== 'select') {
      this.inputElement.select()
    }
  }

  createInputElement() {
    if (this.fieldTypeValue === 'select') {
      this.inputElement = document.createElement('select')
      this.inputElement.className = 'w-full bg-white border-2 border-blue-400 rounded px-2 py-1 text-sm focus:outline-none focus:border-blue-600'

      // Add options
      const options = this.selectOptionsValue || []
      options.forEach(option => {
        const optionElement = document.createElement('option')
        optionElement.value = option
        optionElement.textContent = option
        optionElement.selected = option === this.currentValueValue
        this.inputElement.appendChild(optionElement)
      })
    } else {
      this.inputElement = document.createElement('input')
      this.inputElement.type = this.getInputType()
      this.inputElement.value = this.formatForInput(this.currentValueValue)
      this.inputElement.className = 'w-full bg-white border-2 border-blue-400 rounded px-2 py-1 text-sm text-right focus:outline-none focus:border-blue-600'
    }

    // Event listeners
    this.inputElement.addEventListener('blur', this.saveValue.bind(this))
    this.inputElement.addEventListener('keydown', this.handleKeydown.bind(this))

    if (this.fieldTypeValue !== 'select') {
      this.inputElement.addEventListener('input', this.validateInput.bind(this))
    }

    this.displayTarget.appendChild(this.inputElement)
  }

  getInputType() {
    switch(this.fieldTypeValue) {
      case 'currency':
      case 'rate':
      case 'integer':
        return 'text' // Use text to allow formatting
      case 'text':
        return 'text'
      default:
        return 'text'
    }
  }

  formatForInput(value) {
    if (value === null || value === undefined || value === '') return ''

    switch(this.fieldTypeValue) {
      case 'currency':
      case 'rate':
        // Strip currency symbols and commas for editing
        if (typeof value === 'number') {
          return value.toString()
        }
        // Remove $, commas, and other non-numeric chars except decimal and minus
        return value.toString().replace(/[$,]/g, '')
      case 'integer':
        if (typeof value === 'number') {
          return Math.round(value).toString()
        }
        // Remove commas and parse as integer
        const cleaned = value.toString().replace(/[,]/g, '')
        const parsed = parseInt(cleaned)
        return isNaN(parsed) ? cleaned : parsed.toString()
      case 'text':
      case 'select':
        return value.toString()
      default:
        return value.toString()
    }
  }

  handleKeydown(event) {
    switch(event.key) {
      case 'Enter':
        this.saveValue()
        break
      case 'Escape':
        this.cancelEdit()
        break
      case 'Tab':
        this.saveValue()
        // Let tab continue to next cell
        break
    }
  }

  validateInput(event) {
    // Real-time validation as user types
    const value = event.target.value

    if (this.fieldTypeValue === 'currency' || this.fieldTypeValue === 'rate') {
      // Allow digits, decimal point, and negative sign
      const validPattern = /^-?\d*\.?\d*$/
      if (!validPattern.test(value)) {
        event.target.value = value.slice(0, -1) // Remove last character
      }
    } else if (this.fieldTypeValue === 'integer') {
      // Allow only digits and negative sign
      const validPattern = /^-?\d*$/
      if (!validPattern.test(value)) {
        event.target.value = value.slice(0, -1) // Remove last character
      }
    }
  }

  saveValue() {
    if (!this.isEditing) return

    const rawValue = this.inputElement.value
    const processedValue = this.processInputValue(rawValue)

    if (this.isValidValue(processedValue)) {
      // Update current value and display
      this.currentValueValue = processedValue
      this.updateDisplay()

      // Update highlighting based on comparison with original
      this.updateHighlighting()

      // Notify editor controller of change
      if (this.editorController) {
        this.editorController.fieldValueChanged(this.fieldPathValue, processedValue)
      }
    } else {
      this.showValidationError()
      return // Keep editing
    }

    this.finishEditing()
  }

  cancelEdit() {
    if (!this.isEditing) return
    this.finishEditing()
  }

  processInputValue(rawValue) {
    if (rawValue === '' || rawValue === null || rawValue === undefined) {
      return null
    }

    switch(this.fieldTypeValue) {
      case 'currency':
      case 'rate':
        // Remove currency symbols, parse as float
        const numValue = parseFloat(rawValue.replace(/[$,]/g, ''))
        return isNaN(numValue) ? null : numValue
      case 'integer':
        const intValue = parseInt(rawValue.replace(/[,]/g, ''))
        return isNaN(intValue) ? null : intValue
      case 'text':
      case 'select':
        return rawValue.trim()
      default:
        return rawValue
    }
  }

  isValidValue(value) {
    switch(this.fieldTypeValue) {
      case 'currency':
      case 'rate':
        return value === null || (typeof value === 'number' && !isNaN(value))
      case 'integer':
        return value === null || (typeof value === 'number' && Number.isInteger(value))
      case 'text':
      case 'select':
        return true // All text/select values are valid
      default:
        return true
    }
  }

  updateDisplay() {
    const formattedValue = this.formatForDisplay(this.currentValueValue)
    const valueDisplay = this.displayTarget.querySelector('.value-display')
    if (valueDisplay) {
      valueDisplay.textContent = formattedValue
    }
  }

  formatForDisplay(value) {
    if (value === null || value === undefined || value === '') return ''

    switch(this.fieldTypeValue) {
      case 'currency':
        return this.formatCurrency(value)
      case 'rate':
        const precision = this.precisionValue || 3
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: precision,
          maximumFractionDigits: precision
        }).format(value)
      case 'integer':
        return new Intl.NumberFormat('en-US').format(value)
      case 'text':
      case 'select':
        return value.toString()
      default:
        return value.toString()
    }
  }

  formatCurrency(value) {
    const precision = this.precisionValue || 2
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: precision,
      maximumFractionDigits: precision
    }).format(value)
  }

  showInput() {
    // Hide the display content and show input
    const valueDisplay = this.displayTarget.querySelector('.value-display')
    if (valueDisplay) {
      valueDisplay.style.display = 'none'
    }
    this.inputElement.style.display = 'block'
  }

  hideInput() {
    // Show the display content and hide input
    const valueDisplay = this.displayTarget.querySelector('.value-display')
    if (valueDisplay) {
      valueDisplay.style.display = 'inline'
    }
    if (this.inputElement) {
      this.inputElement.remove()
      this.inputElement = null
    }
  }

  finishEditing() {
    this.isEditing = false
    this.element.classList.remove('editing')
    this.hideInput()
  }

  showValidationError() {
    // Flash red border briefly
    this.inputElement.classList.add('border-red-500')
    setTimeout(() => {
      this.inputElement.classList.remove('border-red-500')
      this.inputElement.classList.add('border-blue-400')
    }, 1000)
  }

  // Check if field has changed compared to original value
  isFieldChanged() {
    if (this.readOnlyValue) return false
    const current = this.normalizeValue(this.currentValueValue)
    const original = this.normalizeValue(this.originalValueValue)
    return current !== original
  }

  normalizeValue(value) {
    // Handle null/undefined/empty string normalization
    if (value === null || value === undefined || value === '') return null

    // For numeric fields, normalize to numbers for comparison
    if (this.fieldTypeValue === 'currency' || this.fieldTypeValue === 'rate' || this.fieldTypeValue === 'integer') {
      const numValue = parseFloat(value.replaceAll(",",""))
      return isNaN(numValue) ? null : numValue
    }

    return value?.toString().trim()
  }

  updateHighlighting() {
    const isChanged = this.isFieldChanged()
    const cellElement = this.element.closest('td')

    if (isChanged) {
      cellElement.classList.add('bg-yellow-50', 'border-l-4', 'border-yellow-400')
      //this.showChangeIndicator()
    } else {
      cellElement.classList.remove('bg-yellow-50', 'border-l-4', 'border-yellow-400')
      //this.hideChangeIndicator()
    }
  }

  showChangeIndicator() {
    // Add or show the change indicator dot
    let indicator = this.displayTarget.querySelector('.change-indicator')
    if (!indicator) {
      indicator = document.createElement('div')
      indicator.className = 'change-indicator absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full border border-white z-10'
      this.displayTarget.appendChild(indicator)
    }

    // Show original value hint
    this.showOriginalValueHint()
  }

  hideChangeIndicator() {
    const indicator = this.displayTarget.querySelector('.change-indicator')
    if (indicator) {
      indicator.remove()
    }
    this.hideOriginalValueHint()
  }

  showOriginalValueHint() {
    let hint = this.displayTarget.querySelector('.original-value-hint')
    if (!hint && this.originalValueValue !== null && this.originalValueValue !== undefined && this.originalValueValue !== '') {
      //hint = document.createElement('div')
      //hint.className = 'original-value-hint text-xs text-yellow-600 mt-1 italic'
      //hint.textContent = `Was: ${this.formatForDisplay(this.originalValueValue)}`
      //this.displayTarget.appendChild(hint)
    }
  }

  hideOriginalValueHint() {
    const hint = this.displayTarget.querySelector('.original-value-hint')
    if (hint) {
      hint.remove()
    }
  }

  findEditorController() {
    // Look for generic data attribute specifying parent controller
    const editorElement = this.element.closest("[data-field-editor-parent]")
    if (editorElement) {
      const controllerName = editorElement.dataset.fieldEditorParent
      return this.application.getControllerForElementAndIdentifier(editorElement, controllerName)
    }
    // Fallback for backward compatibility
    const rateSheetElement = this.element.closest("[data-controller*='rate-sheet-editor']")
    if (rateSheetElement) {
      return this.application.getControllerForElementAndIdentifier(rateSheetElement, "rate-sheet-editor")
    }
    return null
  }
}
